import { randomUUID } from '@/lib/random-id';
import {
  safeLocalStorageGetItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
} from '@/lib/safe-storage';

const CART_SESSION_KEY = 'checkout_cart_session:v1';

export type CartSession = {
  cart_id: string;
  started_at: string;
};

function readSession(): CartSession | null {
  try {
    const raw = safeLocalStorageGetItem(CART_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CartSession>;
    if (
      typeof parsed?.cart_id === 'string' &&
      parsed.cart_id &&
      typeof parsed?.started_at === 'string' &&
      parsed.started_at
    ) {
      return { cart_id: parsed.cart_id, started_at: parsed.started_at };
    }
    return null;
  } catch {
    return null;
  }
}

function writeSession(session: CartSession): void {
  try {
    safeLocalStorageSetItem(CART_SESSION_KEY, JSON.stringify(session));
  } catch {
    // ignore quota / private mode
  }
}

/** Create a cart session if none exists (first item added / checkout with items). */
export function ensureCartSession(): CartSession | null {
  if (typeof window === 'undefined') return null;
  const existing = readSession();
  if (existing) return existing;
  const session: CartSession = {
    cart_id: randomUUID(),
    started_at: new Date().toISOString(),
  };
  writeSession(session);
  return session;
}

export function getCartSession(): CartSession | null {
  if (typeof window === 'undefined') return null;
  return readSession();
}

export function clearCartSession(): void {
  if (typeof window === 'undefined') return;
  try {
    safeLocalStorageRemoveItem(CART_SESSION_KEY);
  } catch {
    // ignore
  }
}

export function getCartAgeSeconds(now = Date.now()): number | undefined {
  const session = getCartSession();
  if (!session?.started_at) return undefined;
  const startedMs = Date.parse(session.started_at);
  if (Number.isNaN(startedMs)) return undefined;
  return Math.max(0, Math.floor((now - startedMs) / 1000));
}
